import { createSlice } from '@reduxjs/toolkit';

import { IntegrationReducerState, MPMLicense } from 'lib/types';
import {
    mpmIntegrationToIntegration,
    mpmLicenseToLicense,
    removeItemFromArray,
} from 'lib/helper-functions';
import { Status } from 'reducers/enum';

import {
    retrieveIntegrationsThunk as retrieveIntegrations,
    createIntegrationThunk as createIntegration,
    updateIntegrationThunk as updateIntegration,
    deleteIntegrationThunk as deleteIntegration,
    retrieveLicenseThunk as retrieveLicense,
    retrieveLicensesThunk as retrieveLicenses,
    retrieveAllLicensesThunk as retrieveAllLicenses,
    createLicenseThunk as createLicense,
    updateLicenseThunk as updateLicense,
} from 'reducers/integrations/actions';

const initialState: IntegrationReducerState = {
    byId: {},
    allIds: [],
    retrieveIntegrationsStatus: Status.Idle,
    createUpdateOrDeleteIntegrationStatus: Status.Idle,
    retrieveLicenseStatus: Status.Idle,
    retrieveLicensesStatus: Status.Idle,
    retrieveAllLicensesStatus: Status.Idle,
    createOrUpdateLicenseStatus: Status.Idle,
    error: '',
};

const integrationSlice = createSlice({
    name: 'integrations',
    initialState,
    reducers: {
        resetCreateIntegrationStatus: (state, action) => {
            state.createUpdateOrDeleteIntegrationStatus = Status.Idle;
        },
        resetCreateOrUpdateLicenseStatus: (state, action) => {
            state.createOrUpdateLicenseStatus = Status.Idle;
        },
        removeError: (state) => {
            state.error = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveIntegrations.pending, (state, action) => {
                state.retrieveIntegrationsStatus = Status.Pending;
            })
            .addCase(retrieveIntegrations.rejected, (state, action) => {
                state.error = action.payload as string;
                state.retrieveIntegrationsStatus = Status.Finished;
            })
            .addCase(retrieveIntegrations.fulfilled, (state, action) => {
                state.error = '';
                
                const integrations = action.payload.map((integration) =>
                    mpmIntegrationToIntegration(integration)
                );

                integrations.forEach((integration) => {
                    state.byId[integration.id] = {
                        ...state.byId[integration.id],
                        ...integration,
                    };
                });

                state.allIds = integrations.map(
                    (integration) => integration.id
                );
                state.retrieveIntegrationsStatus = Status.Finished;
            })
            .addCase(retrieveLicense.pending, (state, action) => {
                state.retrieveLicenseStatus = Status.Pending;
            })
            .addCase(retrieveLicense.rejected, (state, action) => {
                state.error = action.payload as string;
                state.retrieveLicenseStatus = Status.Finished;
            })
            .addCase(retrieveLicense.fulfilled, (state, action) => {
                state.error = '';
                
                const { integrationId } = action.meta.arg;
                const mpmLicense = action.payload;

                const mappedLicense = mpmLicenseToLicense(mpmLicense);

                // Replace the existing license, if any, with the newly retrieved license
                // If none are found, simply inserts the license
                const licenses = state.byId[integrationId].licenses;
                const existingLicense = licenses?.find(
                    (license) => license.id === mappedLicense.id
                );

                const updatedLicenses = [
                    ...licenses.filter(
                        (license) => license.id !== existingLicense?.id
                    ),
                    mappedLicense,
                ];
                state.byId[integrationId].licenses = updatedLicenses;

                state.retrieveLicenseStatus = Status.Finished;
            })
            .addCase(retrieveLicenses.pending, (state, action) => {
                state.retrieveLicensesStatus = Status.Pending;
            })
            .addCase(retrieveLicenses.rejected, (state, action) => {
                state.error = action.payload as string;
                state.retrieveLicensesStatus = Status.Finished;
            })
            .addCase(retrieveLicenses.fulfilled, (state, action) => {
                state.error = '';

                const { integrationId } = action.meta.arg;
                const mpmLicenses = action.payload;

                const mappedLicenses = mpmLicenses.map((license) =>
                    mpmLicenseToLicense(license)
                );

                state.byId[integrationId].licenses = mappedLicenses;
                state.retrieveLicensesStatus = Status.Finished;
            })
            .addCase(retrieveAllLicenses.pending, (state, action) => {
                state.retrieveAllLicensesStatus = Status.Pending;
            })
            .addCase(retrieveAllLicenses.rejected, (state, action) => {
                state.error = action.payload as string;
                state.retrieveAllLicensesStatus = Status.Finished;
            })
            .addCase(retrieveAllLicenses.fulfilled, (state, action) => {
                state.error = '';

                const licensesMap = action.payload;

                licensesMap.forEach((item) => {
                    const integrationId = item.integrationId;
                    const mpmLicenses = item.licenses;

                    const licenses = mpmLicenses.map((license) =>
                        mpmLicenseToLicense(license)
                    );
                    state.byId[integrationId].licenses = licenses;
                });

                state.retrieveAllLicensesStatus = Status.Finished;
            })
            .addCase(createIntegration.pending, (state, action) => {
                state.createUpdateOrDeleteIntegrationStatus = Status.Pending;
            })
            .addCase(createIntegration.rejected, (state, action) => {
                state.error = action.payload as string;
                state.createUpdateOrDeleteIntegrationStatus = Status.Finished;
            })
            .addCase(createIntegration.fulfilled, (state, action) => {
                state.error = '';

                const mpmIntegration = action.payload;
                const integration = mpmIntegrationToIntegration(mpmIntegration);

                state.byId = {
                    ...state.byId,
                    [integration.id]: { ...integration, licenses: [] },
                };
                state.allIds = [...state.allIds, integration.id];
                state.createUpdateOrDeleteIntegrationStatus = Status.Finished;                 
            })

            .addCase(updateIntegration.pending, (state, action) => {
                state.createUpdateOrDeleteIntegrationStatus = Status.Pending;
            })
            .addCase(updateIntegration.rejected, (state, action) => {
                state.error = action.payload as string;
                state.createUpdateOrDeleteIntegrationStatus = Status.Finished;
            })
            .addCase(updateIntegration.fulfilled, (state, action) => {
                state.error = '';

                const {
                    integration: { id: integrationId },
                } = action.meta.arg;
                const integrationData = action.payload;

                const mappedIntegrationData = {
                    name: integrationData.name,
                    licenseTypes: integrationData.licenseTypes.map(
                        (type) => type.name
                    ),
                };

                state.byId = {
                    ...state.byId,
                    [integrationId]: {
                        ...state.byId[integrationId],
                        ...mappedIntegrationData,
                    },
                };

                state.createUpdateOrDeleteIntegrationStatus = Status.Finished;
            })
            .addCase(deleteIntegration.pending, (state, action) => {
                state.createUpdateOrDeleteIntegrationStatus = Status.Pending;
            })
            .addCase(deleteIntegration.rejected, (state, action) => {
                state.error = action.payload as string;
                state.createUpdateOrDeleteIntegrationStatus = Status.Finished;
            })
            .addCase(deleteIntegration.fulfilled, (state, action) => {
                state.error = '';

                const { integrationId } = action.meta.arg;
                delete state.byId[integrationId];
                removeItemFromArray(integrationId, state.allIds);
            })
            .addCase(createLicense.pending, (state, action) => {
                state.createOrUpdateLicenseStatus = Status.Pending;
            })
            .addCase(createLicense.rejected, (state, action) => {
                state.error = action.payload as string;
                state.createOrUpdateLicenseStatus = Status.Finished;
            })
            .addCase(createLicense.fulfilled, (state, action) => {
                state.error = '';

                const { integrationId } = action.meta.arg;
                const mpmLicense = action.payload;
                const license = mpmLicenseToLicense(mpmLicense);

                state.byId[integrationId].licenses = [
                    ...state.byId[integrationId].licenses,
                    license,
                ];

                state.createOrUpdateLicenseStatus = Status.Finished;
            })
            .addCase(updateLicense.pending, (state, action) => {
                state.createOrUpdateLicenseStatus = Status.Pending;
            })
            .addCase(updateLicense.rejected, (state, action) => {
                state.error = action.payload as string;
                state.createOrUpdateLicenseStatus = Status.Finished;
            })
            .addCase(updateLicense.fulfilled, (state, action) => {
                state.error = '';
                
                const licenseData = mpmLicenseToLicense(
                    action.payload as MPMLicense
                );

                const { integrationId, licenseId } = action.meta.arg;

                const license = state.byId[integrationId].licenses?.find(
                    (license) => license.id === licenseId
                );

                const licenseIndex = state.byId[
                    integrationId
                ].licenses?.findIndex((license) => license.id === licenseId);

                if (licenseIndex !== null) {
                    state.byId[integrationId].licenses[licenseIndex] = {
                        ...license,
                        ...licenseData,
                    };
                }

                state.createOrUpdateLicenseStatus = Status.Finished;
            });
    },
});

export default integrationSlice.reducer;
